import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import get from "lodash/get";
import set from "lodash/set";
import cloneDeep from "lodash/cloneDeep";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SiteContext = createContext(null);

export const useSite = () => useContext(SiteContext);

export const SiteProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then((r) => setContent(r.data)).catch(() => setContent({}));
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAdmin(r.data);
      } catch {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setAdmin(r.data);
        } catch {
          setAdmin(false);
        }
      }
    })();
  }, []);

  const patch = useCallback((path, value) => {
    setContent((c) => {
      const copy = cloneDeep(c);
      set(copy, path, value);
      return copy;
    });
    setDirty(true);
  }, []);

  const addItem = useCallback((path, item) => {
    setContent((c) => {
      const copy = cloneDeep(c);
      const arr = get(copy, path, []);
      arr.push(item);
      set(copy, path, arr);
      return copy;
    });
    setDirty(true);
  }, []);

  const removeItem = useCallback((path, index) => {
    setContent((c) => {
      const copy = cloneDeep(c);
      const arr = get(copy, path, []);
      arr.splice(index, 1);
      set(copy, path, arr);
      return copy;
    });
    setDirty(true);
  }, []);

  const save = async () => {
    try {
      try {
        await axios.put(`${API}/content`, content, { withCredentials: true });
      } catch (e) {
        if (e.response?.status !== 401) throw e;
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        await axios.put(`${API}/content`, content, { withCredentials: true });
      }
      setDirty(false);
      toast.success("Saved — your changes are live.");
    } catch {
      toast.error("Could not save. Please sign in again.");
    }
  };

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setAdmin(r.data);
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setAdmin(false);
    toast.success("Signed out.");
  };

  return (
    <SiteContext.Provider
      value={{ content, patch, addItem, removeItem, dirty, save, admin, login, logout, loginOpen, setLoginOpen }}
    >
      {children}
    </SiteContext.Provider>
  );
};
